import * as Sentry from "@sentry/nextjs";

const DEFAULT_TRACES_SAMPLE_RATE = 0.2;
const DEFAULT_PROFILES_SAMPLE_RATE = 0;

const NOISY_SENTRY_MESSAGE_PATTERNS = [
  /^aborted$/i,
  /\bAbortError\b/i,
  /failed to pipe response/i,
  /\bChunkLoadError\b/i,
  /Loading chunk [\w-]+ failed/i,
  /ResizeObserver loop completed with undelivered notifications/i,
  /ResizeObserver loop limit exceeded/i,
];

function normalizeEnvironmentName(prefix: string, value: string | undefined) {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  if (/^(development|preview|production)$/i.test(trimmed)) {
    return `${prefix}-${trimmed.toLowerCase()}`;
  }
  return trimmed;
}

function resolveReleaseName() {
  const explicitRelease = process.env.SENTRY_RELEASE?.trim();
  if (explicitRelease) return explicitRelease;
  const commitSha =
    process.env.VERCEL_GIT_COMMIT_SHA?.trim() || process.env.GITHUB_SHA?.trim();
  return commitSha || undefined;
}

function shouldIgnoreMonitoringMessage(message: string | undefined) {
  if (!message) return false;
  return NOISY_SENTRY_MESSAGE_PATTERNS.some((pattern) => pattern.test(message));
}

function getEventMessage(event: Sentry.Event, hint?: Sentry.EventHint) {
  const originalMessage =
    hint?.originalException instanceof Error
      ? hint.originalException.message
      : typeof hint?.originalException === "string"
        ? hint.originalException
        : undefined;
  if (originalMessage) return originalMessage;

  const exception = event.exception?.values?.[0];
  if (exception?.type && exception.value) {
    return `${exception.type}: ${exception.value}`;
  }
  if (exception?.value) return exception.value;
  if (event.logentry?.message) return event.logentry.message;
  return event.message;
}

const clampRate = (value: string | undefined, fallback: number) => {
  const parsed = typeof value === "string" ? Number(value) : Number.NaN;
  if (!Number.isFinite(parsed)) return fallback;
  if (parsed < 0) return 0;
  if (parsed > 1) return 1;
  return parsed;
};

const sentryDsn = (process.env.SENTRY_DSN || "").trim();
const environment = normalizeEnvironmentName(
  "web",
  process.env.SENTRY_ENVIRONMENT ||
    process.env.VERCEL_ENV ||
    process.env.NODE_ENV ||
    "development",
);
const release = resolveReleaseName();
const sentryEnabled = sentryDsn.length > 0;
const tracesSampleRate = clampRate(
  process.env.SENTRY_TRACES_SAMPLE_RATE,
  DEFAULT_TRACES_SAMPLE_RATE,
);
const profilesSampleRate = clampRate(
  process.env.SENTRY_PROFILES_SAMPLE_RATE,
  DEFAULT_PROFILES_SAMPLE_RATE,
);

const debugEnabled =
  (process.env.SENTRY_DEBUG || "").toLowerCase() === "true" ||
  process.env.SENTRY_DEBUG === "1";
const enableConsoleLogs = debugEnabled;
const prodIntegrations = enableConsoleLogs
  ? [
      Sentry.consoleLoggingIntegration({
        levels: ["error"],
      }),
    ]
  : [];

if (!sentryEnabled && typeof console !== "undefined") {
  console.error(
    "[sentry] SENTRY_DSN is not set; Sentry is disabled for the web app",
  );
}

export const sentryOptions = {
  dsn: sentryDsn || undefined,
  enabled: sentryEnabled,
  environment,
  release,
  tracesSampleRate,
  profilesSampleRate,
  debug: debugEnabled,
  sendDefaultPii: false,
  enableLogs: enableConsoleLogs,
  integrations: prodIntegrations,
  beforeSend(event: Sentry.Event, hint: Sentry.EventHint) {
    const message = getEventMessage(event, hint);
    if (shouldIgnoreMonitoringMessage(message)) {
      return null;
    }
    return event;
  },
};
